import { Button } from '@utilitywarehouse/hearth-react';
import {
  ChevronLeftMediumIcon,
  ChevronRightMediumIcon,
} from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import {
  FC,
  ReactNode,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import type { CustomArrowProps, Settings } from 'react-slick';
import SliderImport from 'react-slick';
import 'slick-carousel/slick/slick.css';

import { useSyncState } from '@hooks/useSyncState';

import './style.scss';

export interface CarouselRef {
  slickPrev: () => void;
  slickNext: () => void;
  slickGoTo: (index: number) => void;
}

interface CarouselProps {
  id: string;
  children: ReactNode;
  settings?: Settings;
  ref?: Ref<CarouselRef>;
}

enum Direction {
  Previous = 'prev',
  Next = 'next',
}

interface ArrowProps extends CustomArrowProps {
  direction: Direction;
}

// @ts-expect-error https://github.com/akiran/react-slick/issues/2444
const Slider = SliderImport.default ?? SliderImport;

const Arrow: FC<ArrowProps> = ({
  direction = 'prev',
  onClick,
  className = '',
}) => {
  // use Slick's own "disabled" logic, but avoid their own styles and theming
  const isDisabled = className.includes('slick-disabled');
  const label = direction === Direction.Next ? 'Next' : 'Previous';

  return (
    <Button
      aria-label={label}
      variant="ghost"
      className={clsx('carousel__Arrow', `carousel__Arrow-${direction}`, {
        'carousel__Arrow-disabled': isDisabled,
      })}
      disabled={isDisabled}
      onClick={onClick}
    >
      {direction === Direction.Previous ? (
        <ChevronLeftMediumIcon />
      ) : (
        <ChevronRightMediumIcon />
      )}
      <span className="carousel__Arrow-label">{label}</span>
    </Button>
  );
};

const Carousel = ({ id, children, settings = {}, ref }: CarouselProps) => {
  const [slideIndex, setSlideIndex] = useSyncState(
    id,
    settings.initialSlide ?? 0
  );
  const sliderRef = useRef<SliderImport | null>(null);

  useImperativeHandle(ref, () => ({
    slickPrev: () => sliderRef.current?.slickPrev(),
    slickNext: () => sliderRef.current?.slickNext(),
    slickGoTo: (index: number) => sliderRef.current?.slickGoTo(index),
  }));

  useEffect(() => {
    if (!sliderRef.current) return;

    sliderRef.current.slickGoTo(slideIndex);
  }, [slideIndex, sliderRef]);
  /**
   * available settings:
   * https://react-slick.neostack.com/docs/api
   */
  const defaultSettings = {
    dots: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    infinite: false,
    variableWidth: false,
    swipeToSlide: true,
    centerMode: false,
    easing: 'ease-out',
    slide: 'div.carousel__Slide',
    nextArrow: <Arrow direction={Direction.Next} />,
    prevArrow: <Arrow direction={Direction.Previous} />,
  };

  const onBeforeChange = (current: number, next: number) => {
    if (settings.beforeChange) settings.beforeChange(current, next);

    setSlideIndex(next);
  };

  return (
    <Slider
      ref={sliderRef}
      data-testid={id}
      className="carousel"
      {...defaultSettings}
      {...settings}
      beforeChange={onBeforeChange}
    >
      {children}
    </Slider>
  );
};

Carousel.displayName = 'Carousel';

export { Carousel };
